import React from "react";
import moment from "moment";
import ReactMultiSelectCheckboxes from "react-multiselect-checkboxes";
import styles from "./FrequencyBucketTable.module.css";

// Shared by the vehicle popovers (depot vehicles + healthy / unhealthy vehicles).

// key is regNumber; RegNumber is the casing used by the other vehicle APIs
export const getRegNumber = (v) => v.regNumber ?? v.RegNumber;

// "2026-10-07" -> "07 Oct 2026"
export const formatPopoverDate = (date) => {
    const parsed = date ? moment(date, "YYYY-MM-DD") : null;
    return parsed?.isValid() ? parsed.format("DD MMM YYYY") : "-";
};

// dropdown options: one per vehicle; the label carries the reg number so it is searchable too
export const toVehicleOptions = (vehicles) =>
    vehicles
        .filter((v) => v.vin)
        .map((v) => ({
            value: v.vin,
            label: getRegNumber(v) ? `${v.vin} · ${getRegNumber(v)}` : v.vin
        }));

// nothing selected = all vehicles
export const filterBySelectedVehicles = (vehicles, selected) => {
    if (!selected.length) return vehicles;
    const selectedVins = new Set(selected.map((o) => o.value));
    return vehicles.filter((v) => selectedVins.has(v.vin));
};

// checkbox + label on one line (same pattern as Filter/CustomSelect)
const VehicleOption = ({ data, isSelected, innerRef, innerProps }) => (
    <div ref={innerRef} {...innerProps} className={styles.vehicleOption}>
        <input type="checkbox" checked={isSelected} readOnly />
        <span>{data.label}</span>
    </div>
);

// "Select All" is always the first row; clicking it again deselects all
const VehicleMenuList = ({ children, options, getValue, setValue }) => {
    const allSelected = options.length > 0 && getValue().length === options.length;

    return (
        <div className={styles.vehicleMenuList}>
            {options.length > 0 && (
                <div
                    className={`${styles.vehicleOption} ${styles.vehicleSelectAll}`}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={() => setValue(allSelected ? [] : [...options], "select-option")}
                >
                    <input type="checkbox" checked={allSelected} readOnly />
                    <span>Select All</span>
                </div>
            )}
            {children}
        </div>
    );
};

const VEHICLE_SELECT_COMPONENTS = { Option: VehicleOption, MenuList: VehicleMenuList };

// search box, option list and menu all share one width (see .vehicleMenuList)
const VEHICLE_MENU_WIDTH = "min(270px, 80vw)";

const VEHICLE_SELECT_STYLES = {
    control: (provided) => ({ ...provided, width: VEHICLE_MENU_WIDTH, minWidth: 0 }),
    dropdownButton: (provided) => ({ ...provided, minWidth: 170 })
};

/* multi-select vehicle dropdown with built-in search */
export default function VehicleMultiSelect({ options, value, onChange }) {
    return (
        <div className={styles.vehicleSelect}>
            <ReactMultiSelectCheckboxes
                options={options}
                value={value}
                onChange={(v) => onChange(v || [])}
                placeholderButtonLabel="Select Vehicles"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={VEHICLE_SELECT_COMPONENTS}
                styles={VEHICLE_SELECT_STYLES}
            />
        </div>
    );
}
